import { create } from 'zustand';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantContextPolicy,
  GlobalAssistantSuggestMatch,
} from './global-ai-assistant-api';
import type { HeadlessGeneratedPage } from './global-ai-generate-headless';
import type { ReasoningTrace } from './reasoning-trace';
import type { ReportOfferCardFields } from './report-offer-launch-key';

/**
 * `option selected` source: `tool` when the assistant moved the user mid-turn
 * (the `navigate` tool, or `apply_filters` opening a discovery page), `click`
 * when the user followed a link in an answer.
 */
export type OptionTrigger = 'tool' | 'click';

/**
 * Actions the headless `GlobalAiAssistantEngine` publishes for the chat
 * skins (floating drawer + assistant homepage).
 *
 * The registered value IS the `AssistantEngine` instance (assistant-engine-core.ts),
 * which `implements` this type: per-mount stable, so subscribing to `api`
 * never churns on engine re-renders, and always the freshest closures because
 * the instance's deps are refreshed in the engine's render body. Observable
 * turn state lives as plain store fields alongside it.
 *
 * The field is nulled again on unmount (`AssistantEngine.dispose`) — the skins
 * `?.`-guard on it and conversation persistence reads `api !== null` as its
 * "engine mounted" signal. A bound method captured BEFORE the unmount stays
 * callable and silently no-ops (see the class's `disposed` field).
 */
export type GlobalAiEngineApi = {
  /**
   * Validate + submit a typed prompt (append user message, track, run the
   * turn) — the drawer's old `handleSubmit` with the draft passed in.
   * Returns false synchronously when rejected (empty / turn already
   * running / report offer launching) so the calling skin keeps its draft.
   */
  submitPrompt: (rawPrompt: string) => boolean;
  startAssistantTurn: (
    prompt: string,
    options?: { silent?: boolean; suppressReportOffer?: boolean }
  ) => Promise<void>;
  navigateToMatch: (
    match: GlobalAssistantSuggestMatch,
    options?: {
      standard?: boolean;
      prompt?: string;
      skipFilterApply?: boolean;
      resolvedFilters?: TalentDiscoveryAiFilterSearchResponseV2;
      trigger?: OptionTrigger;
      answerMessageId?: string;
    }
  ) => Promise<{ navigated: boolean }>;
  startCustomPageCreate: (
    entityType: CustomDashAssistantEntityType,
    options?: {
      prompt?: string;
      contextPolicy?: GlobalAssistantContextPolicy;
      skipBuild?: boolean;
      answerMessageId?: string;
    }
    // Async: the unsaved-changes guard can open the in-app dialog.
  ) => Promise<{ ok: boolean; targetPath?: string }>;
  /**
   * Lazy click-time page generation for a build chip without a pre-generated
   * layout — runs the page-gen stream headlessly (live reasoning trace) before
   * navigating to a complete draft (resolve-first). Null page → the offer
   * wiring falls back to the legacy resume flow.
   */
  generateForBuildClick: (
    prompt: string,
    entityType: CustomDashAssistantEntityType
  ) => Promise<{
    page: HeadlessGeneratedPage | null;
    buildTrace?: { traceId: string; trace: ReasoningTrace };
  }>;
  /**
   * Builds the consume-once `onSeeded` callback a resolve-first build stashes
   * with its generated page (landing narration once the widget ids mint).
   */
  beginSeededBuildNarration: (params: {
    prompt: string;
    targetPathname: string;
    filters?: TalentDiscoveryAiFilterSearchResponseV2;
    resumeTrace?: { traceId: string; trace: ReasoningTrace };
  }) => (seededItems: PositionedItem[]) => void;
  /**
   * The inline `[phrase](offer:geo_tax)` narration link: write the GLOBAL
   * geography taxonomy preference, then RE-RESOLVE the current prompt under
   * it and apply the fresh artifact. `nextTaxonomyId` comes from the turn's
   * `filter_response` artifact — never from the model's visible text.
   */
  switchGeographyTaxonomy: (
    nextTaxonomyId: number,
    prompt?: string,
    reaskOnly?: boolean
  ) => Promise<void>;
  handleReportOfferClick: (
    offer: ReportOfferCardFields,
    prompt: string | undefined,
    messageId: string
  ) => Promise<void>;
  armNavResume: (next: {
    continuationPrompt: string;
    pageName: string;
    expectedPath: string;
  }) => void;
  armCreatePageResume: (next: { seed: string; expectedPath: string }) => void;
  armBuildHere: () => void;
  /**
   * User-initiated stop: abort the in-flight turn and any page-generation
   * stream WITHOUT closing the drawer. Distinct from `closeDrawer`, which
   * aborts as a side effect of tearing the surface down. No-op when nothing
   * is running.
   */
  cancelTurn: () => void;
  /** Clear transcript + engine error + conversation id ("new session"). */
  resetSession: () => void;
};

/**
 * Lifecycle of the CURRENT (or most recent) turn, keyed by `turnId`.
 *
 * `status` is two-state plus `idle` — there is no separate `gating` status
 * for the engine's pre-run taxonomy hold because nothing consumes the
 * distinction: the input disable, the stop affordance, and the autosave all
 * treat the gate window and the streaming loop identically, so `running`
 * covers both from `beginTurn` on. `stopping` is the user-initiated cancel
 * window (abort signalled, runner still unwinding): still "running" to every
 * derived reader, so the turn-end autosave cannot fire until `endTurn` — the
 * transcript may still gain its Stopped marker.
 *
 * `endTurn` keeps the record (status `idle`) instead of nulling it so
 * post-turn consumers — correction surfacing compares an adjustment's
 * timestamp against the turn it belongs to — can still read `startedAt`
 * after the turn finished. `turn === null` means no turn has run since the
 * engine (re)mounted.
 */
type GlobalAiTurnRecord = {
  turnId: string;
  status: 'running' | 'stopping' | 'idle';
  /** Epoch ms the turn began (beginTurn call time). */
  startedAt: number;
};

/**
 * One-shot cross-navigation continuation, armed by an offer click and
 * consumed exactly once — by the location-keyed effect in
 * `useGlobalAiTurnContinuation` (`nav_resume` / `create_page_resume`) or by
 * the runner's `create_custom_page` handle (`build_here`). A single slot with
 * consume-once semantics closes two failure classes: a consumed continuation
 * can never fire twice, and a stale arm can never survive a newer click
 * (arming overwrites — last click wins). A `build_here` also cannot outlive
 * its turn — `endTurn` drops it at the idle transition (see there), so a turn
 * that dies before reaching `create_custom_page` leaves nothing armed. The
 * cross-navigation kinds outlive their turn by design.
 */
export type GlobalAiPendingContinuation =
  | {
      /**
       * Nav-confirm chip click: resume the chained task once the router
       * lands on `expectedPath`, so the resumed turn reads the DESTINATION
       * page context (not the page it left).
       */
      kind: 'nav_resume';
      continuationPrompt: string;
      pageName: string;
      expectedPath: string;
    }
  | {
      /**
       * Build chip (legacy confirm-navigate-resume): the click created an
       * empty draft + navigated; once the router lands on `expectedPath`,
       * fire the pre-formed `seed` (full original prompt + "build it here"
       * system note) as a silent turn. Carries a fully-formed seed — unlike
       * `nav_resume` — because the build resume needs the explicit
       * "build here" instruction built at click time.
       */
      kind: 'create_page_resume';
      seed: string;
      expectedPath: string;
    }
  | {
      /**
       * Confirmed-build signal: the resumed turn's `create_custom_page`
       * call runs in BUILD mode on the current draft instead of offering
       * again. Armed right before the resume turn starts (same-path click,
       * or by the continuation effect when it consumes a
       * `create_page_resume`).
       */
      kind: 'build_here';
    };

type GlobalAiEngineStore = {
  /** Non-null while the headless engine is mounted. */
  api: GlobalAiEngineApi | null;
  turn: GlobalAiTurnRecord | null;
  pendingContinuation: GlobalAiPendingContinuation | null;
  error: string | null;
  reportOfferLaunchingKey: string | null;
};

export const useGlobalAiEngineStore = create<GlobalAiEngineStore>(() => ({
  api: null,
  turn: null,
  pendingContinuation: null,
  error: null,
  reportOfferLaunchingKey: null,
}));

/**
 * Derived "a turn is in flight" read used by every consumer (input disable,
 * host keep-mounted, chip guards, the autosave's edge subscription).
 * `stopping` still counts as running: the runner is unwinding and the
 * transcript may still gain the Stopped marker, so the input must stay
 * disabled and the turn-end autosave must NOT fire yet.
 */
export const selectIsTurnRunning = (
  state: Pick<GlobalAiEngineStore, 'turn'>
): boolean => state.turn !== null && state.turn.status !== 'idle';

/**
 * Start a turn. Callers guard "a turn is already running" themselves (the
 * submit paths bail before appending anything); this simply installs the
 * new live turn record.
 */
export const beginTurn = (turnId: string, startedAt: number): void => {
  useGlobalAiEngineStore.setState({
    turn: { turnId, status: 'running', startedAt },
  });
};

/**
 * User-initiated stop: mark the live turn as unwinding. NO-OP unless
 * `turnId` is the live turn — a stale writer (an OLD engine instance's
 * cancel landing after a remount started a new turn) must not touch the new
 * turn's state. Guarded BEFORE setState so a no-op never notifies
 * subscribers (the autosave edge subscription must see zero transitions
 * from stale writers).
 */
export const markTurnStopping = (turnId: string): void => {
  const { turn } = useGlobalAiEngineStore.getState();
  if (!turn || turn.turnId !== turnId || turn.status !== 'running') {
    // Includes `stopping`: a repeated Stop click is a no-op that must not
    // notify subscribers (zustand notifies on every setState, even when the
    // derived values are unchanged).
    return;
  }
  useGlobalAiEngineStore.setState({ turn: { ...turn, status: 'stopping' } });
};

/**
 * End a turn: the single producer of the running→idle transition the
 * turn-end autosave subscribes to. NO-OP unless `turnId` is the live,
 * still-running turn — this is what structurally retires the remount race
 * where an old engine instance's `finally { setIsTurnRunning(false) }`
 * landed after a remounted engine had started a new turn and re-enabled the
 * input (and fired a premature autosave) mid-turn. Idempotent per turn: the
 * second `endTurn` for the same id is a no-op, so subscribers observe
 * exactly one idle transition per turn.
 *
 * Also the death certificate for a `build_here` arm. Both arm sites arm while
 * no turn is running and start the consuming turn on the very next line, so a
 * `build_here` still armed at ANY turn's idle transition belongs to a turn
 * that died (stop/error) before reaching `create_custom_page`. Leaving it
 * would let the next plain OFFER `create_custom_page` consume it and rebuild
 * whatever page is in edit mode with no chip and no confirmation. Other kinds
 * are cross-navigation resumes that outlive their turn by design.
 */
export const endTurn = (turnId: string): void => {
  const { turn, pendingContinuation } = useGlobalAiEngineStore.getState();
  if (!turn || turn.turnId !== turnId || turn.status === 'idle') {
    return;
  }
  useGlobalAiEngineStore.setState({
    turn: { ...turn, status: 'idle' },
    ...(pendingContinuation?.kind === 'build_here'
      ? { pendingContinuation: null }
      : {}),
  });
};

/**
 * Arm the one-shot continuation. Overwrites whatever was armed: the slot
 * belongs to the LAST offer click (each arm is an explicit user click, and
 * the click guards already block arming while a turn or command is in
 * flight, so overwrite only ever replaces a stale never-consumed arm).
 */
export const armContinuation = (value: GlobalAiPendingContinuation): void => {
  useGlobalAiEngineStore.setState({ pendingContinuation: value });
};

/**
 * Atomic return-and-clear of the armed continuation, filtered by `kind`:
 * a consumer must never swallow a continuation of a kind it doesn't handle
 * (the runner consuming `build_here` must leave an armed `nav_resume`
 * alone). Returns null when nothing (or a different kind) is armed. The
 * second consume for the same arm returns null — consume-once makes a stale
 * arm surviving a pure-offer path unrepresentable; see the `build_here`
 * lifetime on `GlobalAiPendingContinuation`.
 */
export const consumeContinuation = <
  K extends GlobalAiPendingContinuation['kind'],
>(
  kind: K
): Extract<GlobalAiPendingContinuation, { kind: K }> | null => {
  const { pendingContinuation } = useGlobalAiEngineStore.getState();
  if (!pendingContinuation || pendingContinuation.kind !== kind) {
    return null;
  }
  useGlobalAiEngineStore.setState({ pendingContinuation: null });
  return pendingContinuation as Extract<
    GlobalAiPendingContinuation,
    { kind: K }
  >;
};

// Dev-only introspection for the local eval/probe harness (same convention
// as window.__gaEval): lets a headless probe assert the engine registered.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  (
    window as Window & { __gaEngineStore?: typeof useGlobalAiEngineStore }
  ).__gaEngineStore = useGlobalAiEngineStore;
}
